import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, MessageSquare, PhoneOff } from "lucide-react";

const urgencyStyle = {
  emergency: "bg-rose-500/15 text-rose-300 border-rose-500/30",
  high: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  medium: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  low: "bg-slate-500/15 text-slate-300 border-slate-500/30",
};

const statusStyle = {
  new: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  contacted: "bg-orange-500/15 text-orange-300 border-orange-500/30",
  qualified: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  booked: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  won: "bg-emerald-600/30 text-emerald-200 border-emerald-500/50",
  lost: "bg-slate-700 text-slate-400 border-slate-600",
};

export default function Inbox() {
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ caller_name: "", caller_phone: "", address: "", roof_type: "Asphalt Shingle", issue: "", urgency: "medium", estimated_value: 0 });
  const [consentAttestation, setConsentAttestation] = useState(false);
  const [consentChannel, setConsentChannel] = useState("verbal");
  const [consentNote, setConsentNote] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/leads").then(r => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const filtered = leads.filter(l => `${l.caller_name} ${l.caller_phone} ${l.issue} ${l.address}`.toLowerCase().includes(q.toLowerCase()));

  const create = async () => {
    if (!consentAttestation) return;
    try {
      await api.post("/leads", {
        ...form,
        estimated_value: Number(form.estimated_value) || 0,
        consent_attestation: true,
        consent_channel: consentChannel,
        consent_note: consentNote,
      });
      toast.success("Lead added");
      setOpen(false);
      setForm({ caller_name: "", caller_phone: "", address: "", roof_type: "Asphalt Shingle", issue: "", urgency: "medium", estimated_value: 0 });
      setConsentAttestation(false);
      setConsentChannel("verbal");
      setConsentNote("");
      load();
    } catch (e) {
      toast.error("Could not add lead");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-widest text-orange-400 mb-2">Missed Lead Inbox</p>
          <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">All Leads</h1>
        </div>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads…" className="pl-9 w-72 bg-slate-900 border-slate-700 text-white" data-testid="inbox-search" />
          </div>
          <Button onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold" data-testid="inbox-add-lead">
            <Plus className="w-4 h-4 mr-1" /> Add Lead
          </Button>
        </div>
      </header>

      <Card className="bg-slate-900 border-slate-800 overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-slate-800 text-xs font-mono uppercase tracking-widest text-slate-500">
          <div className="col-span-3">Caller</div>
          <div className="col-span-3">Issue</div>
          <div className="col-span-2">Missed</div>
          <div className="col-span-1">Urgency</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-1 text-right">Value</div>
          <div className="col-span-1 text-right">Action</div>
        </div>
        <div>
          {filtered.length === 0 && <p className="p-8 text-center text-slate-500">No leads match.</p>}
          {filtered.map((l) => (
            <div key={l.id} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-slate-800/50 hover:bg-slate-800/40 transition-colors items-center" data-testid={`inbox-row-${l.id}`}>
              <div className="col-span-3 flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                  <PhoneOff className="w-4 h-4 text-orange-400" />
                </div>
                <div className="min-w-0">
                  <p className="font-semibold truncate">{l.caller_name}</p>
                  <p className="text-xs text-slate-500 font-mono truncate">{l.caller_phone}</p>
                </div>
              </div>
              <div className="col-span-3 min-w-0">
                <p className="text-sm text-slate-200 truncate">{l.issue}</p>
                <p className="text-xs text-slate-500 truncate">{l.roof_type} · {l.address}</p>
              </div>
              <div className="col-span-2 text-sm text-slate-400 font-mono">
                {new Date(l.missed_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              </div>
              <div className="col-span-1"><Badge className={`text-xs border ${urgencyStyle[l.urgency]}`}>{l.urgency}</Badge></div>
              <div className="col-span-1"><Badge className={`text-xs border capitalize ${statusStyle[l.status]}`}>{l.status}</Badge></div>
              <div className="col-span-1 text-right font-mono font-bold text-emerald-400">${l.estimated_value.toLocaleString()}</div>
              <div className="col-span-1 text-right">
                <Button size="sm" variant="ghost" onClick={() => navigate(`/app/chat/${l.id}`)} className="text-orange-400 hover:text-orange-300 hover:bg-slate-800" data-testid={`inbox-open-${l.id}`}>
                  <MessageSquare className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100">
          <DialogHeader><DialogTitle>Add missed lead</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Caller name</Label><Input data-testid="add-lead-name" value={form.caller_name} onChange={(e) => setForm({ ...form, caller_name: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
            <div><Label>Phone</Label><Input data-testid="add-lead-phone" value={form.caller_phone} onChange={(e) => setForm({ ...form, caller_phone: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
            <div><Label>Address</Label><Input data-testid="add-lead-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
            <div><Label>Issue</Label><Input data-testid="add-lead-issue" value={form.issue} onChange={(e) => setForm({ ...form, issue: e.target.value })} placeholder="Storm damage inspection" className="bg-slate-800 border-slate-700" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="add-lead-urgency" className="bg-slate-800 border-slate-700"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                    {["low","medium","high","emergency"].map(u => <SelectItem key={u} value={u} className="capitalize">{u}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Estimated value ($)</Label><Input type="number" data-testid="add-lead-value" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} className="bg-slate-800 border-slate-700" /></div>
            </div>

            {/* A2P consent attestation — required before saving a manually-entered lead */}
            <div className="mt-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/30">
              <label className="flex gap-3 items-start cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="add-lead-consent"
                  checked={consentAttestation}
                  onChange={(e) => setConsentAttestation(e.target.checked)}
                  className="mt-1 w-4 h-4 shrink-0 rounded border-slate-600 bg-slate-900 accent-orange-500"
                />
                <span className="text-xs text-slate-300 leading-relaxed">
                  I confirm the homeowner has given <span className="text-white font-semibold">express written or verbal consent</span> to receive SMS from our company at the number above,
                  including the disclosure that message frequency varies, msg &amp; data rates may apply, and they can reply STOP to opt out.
                  <span className="block mt-1 text-slate-500">Required for A2P 10DLC / TCPA compliance before any outbound SMS is sent.</span>
                </span>
              </label>
              {consentAttestation && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-400">How was consent given?</Label>
                    <Select value={consentChannel} onValueChange={setConsentChannel}>
                      <SelectTrigger data-testid="add-lead-consent-channel" className="bg-slate-800 border-slate-700 h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                        <SelectItem value="verbal">Verbal (phone / in person)</SelectItem>
                        <SelectItem value="written">Written (email / signed form)</SelectItem>
                        <SelectItem value="web_form">Web form (previously)</SelectItem>
                        <SelectItem value="sms_reply">Prior SMS opt-in</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Note (optional)</Label>
                    <Input data-testid="add-lead-consent-note" value={consentNote} onChange={(e) => setConsentNote(e.target.value)} placeholder="e.g. called us at 2:15pm 2/4" className="bg-slate-800 border-slate-700 h-9 text-sm" />
                  </div>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={!consentAttestation} data-testid="add-lead-submit" className="bg-orange-500 hover:bg-orange-600 disabled:bg-slate-700 disabled:text-slate-500 text-slate-900 font-bold">
              Add lead
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
